import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from 'sonner';
import { useAppConfig } from '../context/AppConfigContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsManager = () => {
  const { appConfig } = useAppConfig();
  const companyName = appConfig?.company_name || null;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [activeTab, setActiveTab] = useState("particuliers"); // "particuliers" ou "professionnels"
  
  // États pour la recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    type_client: "particulier",
    appartient_a: null // null=moi, "cyril", "ben"
  });

  useEffect(() => {
    loadClients();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== "") {
        loadClients(searchTerm);
      } else {
        loadClients();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const loadClients = async (search = "") => {
    setLoading(search === "" && clients.length === 0); // Ne montrer le loading que si aucun client n'est chargé
    setSearching(search !== "");
    try {
      const url = search ? `${API}/clients?search=${encodeURIComponent(search)}` : `${API}/clients`;
      const response = await axios.get(url);
      // Trier les clients par ordre alphabétique (nom puis prénom)
      const sortedClients = response.data.sort((a, b) => {
        // Normaliser les noms en retirant les espaces superflus et en mettant en minuscules
        const nomA = (a.nom || '').trim().toLowerCase();
        const prenomA = (a.prenom || '').trim().toLowerCase();
        const nomB = (b.nom || '').trim().toLowerCase();
        const prenomB = (b.prenom || '').trim().toLowerCase();
        
        // Comparer d'abord par nom
        const nomCompare = nomA.localeCompare(nomB, 'fr', { sensitivity: 'base' });
        if (nomCompare !== 0) return nomCompare;
        
        // Si les noms sont identiques, comparer par prénom
        return prenomA.localeCompare(prenomB, 'fr', { sensitivity: 'base' });
      });
      setClients(sortedClients);
    } catch (error) {
      console.error("Erreur lors du chargement des clients:", error);
      toast.info("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    loadClients();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Si création, définir automatiquement le type_client selon l'onglet actif
      const dataToSend = editingClient 
        ? formData 
        : { ...formData, type_client: activeTab === "particuliers" ? "particulier" : "professionnel" };
      
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, dataToSend);
      } else {
        await axios.post(`${API}/clients`, dataToSend);
      }
      setShowModal(false);
      setEditingClient(null);
      resetForm();
      await loadClients();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.info("Erreur lors de l'enregistrement du client");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      prenom: client.prenom,
      nom: client.nom,
      telephone: client.telephone || "",
      appartient_a: client.appartient_a || null
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    {  // Action directe sans confirmation
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        toast.success("Client supprimé avec succès");
        await loadClients();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression du client";
        toast.error(`${errorMessage}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      prenom: "",
      nom: "",
      telephone: "",
      type_client: "particulier",
      appartient_a: null
    });
  };

  // Changer le type d'un client (Particulier ↔ Pro)
  const handleChangeClientType = async (clientId, newType) => {
    try {
      await axios.patch(`${API}/clients/${clientId}/type?type_client=${newType}`);
      await loadClients(); // Recharger les clients
      toast.success(`Client changé en "${newType === 'professionnel' ? 'Professionnel' : 'Particulier'}"`);
    } catch (error) {
      console.error("Erreur changement type:", error);
      toast.error("Erreur lors du changement de type");
    }
  };

  // Filtrer les clients selon l'onglet actif
  const filteredClients = clients.filter(client => {
    const type = client.type_client || "particulier";
    return activeTab === "particuliers" ? type === "particulier" : type === "professionnel";
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement des clients...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4" data-testid="clients-manager">
      {/* Header */}
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">👤 Gestion des Clients</h1>
          <p className="text-gray-600 mt-2">
            Gérez les informations de vos clients
          </p>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Rechercher par prénom, nom, adresse, ville, email ou téléphone..."
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                title="Effacer la recherche"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searching && (
            <div className="flex items-center text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
              Recherche...
            </div>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            {clients.length} résultat{clients.length > 1 ? 's' : ''} trouvé{clients.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Onglets Particuliers / Pro */}
      <div className="mb-3">
        <div className="border-b border-gray-200">
          <nav className="flex justify-center space-x-16">
            <button
              onClick={() => setActiveTab("particuliers")}
              className={`${
                activeTab === "particuliers"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-3 px-4 border-b-4 font-bold text-2xl flex items-center space-x-3 transition-all`}
            >
              <span className="text-3xl">👤</span>
              <span>Clients Particuliers</span>
              <span className="ml-3 bg-blue-100 text-blue-600 py-1 px-3 rounded-full text-lg font-bold">
                {clients.filter(c => (c.type_client || 'particulier') === 'particulier').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("professionnels")}
              className={`${
                activeTab === "professionnels"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-3 px-4 border-b-4 font-bold text-2xl flex items-center space-x-3 transition-all`}
            >
              <span className="text-3xl">🏢</span>
              <span>Clients PRO</span>
              <span className="ml-3 bg-purple-100 text-purple-600 py-1 px-3 rounded-full text-lg font-bold">
                {clients.filter(c => (c.type_client || 'particulier') === 'professionnel').length}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === "particuliers" ? "👤 Clients Particuliers" : "🏢 Clients PRO"} ({filteredClients.length})
          </h2>
          <button
            onClick={() => {
              resetForm();
              setEditingClient(null);
              setShowModal(true);
            }}
            className="btn-primary"
            data-testid="add-client-btn"
          >
            {activeTab === "particuliers" ? "➕ Nouveau client" : "➕ Nouveau client PRO"}
          </button>
        </div>
        <div className="card-body">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👤</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun client enregistré
              </h3>
              <p className="text-gray-500 mb-4">
                Commencez par ajouter votre premier client
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setEditingClient(null);
                  setShowModal(true);
                }}
                className="btn-primary"
              >
                Ajouter un client
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50" data-testid={`client-row-${client.id}`}>
                      <td className="py-1">
                        <div className="font-medium text-gray-900">
                          {client.prenom} {client.nom || ''}
                        </div>
                      </td>
                      <td className="py-1">
                        {client.email ? (
                          <a href={`mailto:${client.email}`} className="text-blue-600 hover:text-blue-800">
                            {client.email}
                          </a>
                        ) : (
                          <span className="text-gray-400">Non renseigné</span>
                        )}
                      </td>
                      <td className="py-1">
                        <button
                          onClick={() => handleChangeClientType(
                            client.id,
                            (client.type_client || 'particulier') === 'particulier' ? 'professionnel' : 'particulier'
                          )}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            (client.type_client || 'particulier') === 'particulier'
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                          title={`Changer en ${(client.type_client || 'particulier') === 'particulier' ? 'PRO' : 'Particulier'}`}
                        >
                          {(client.type_client || 'particulier') === 'particulier' ? '👤 Particulier' : '🏢 PRO'}
                        </button>
                      </td>
                      <td className="py-1">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(client)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            data-testid={`edit-client-${client.id}`}
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                            data-testid={`delete-client-${client.id}`}
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" data-testid="client-modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingClient ? "Modifier le client" : "Nouveau client"}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingClient(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-modal-btn"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData(prev => ({...prev, prenom: e.target.value}))}
                    className="form-input"
                    data-testid="client-prenom-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({...prev, nom: e.target.value}))}
                    className="form-input"
                    data-testid="client-nom-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData(prev => ({...prev, telephone: e.target.value}))}
                  className="form-input"
                  placeholder="06 12 34 56 78"
                  data-testid="client-telephone-input"
                />
              </div>

              {/* Section Multi-propriétaires */}
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  👤 Ce client est géré par...
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="appartient_a"
                      checked={!formData.appartient_a}
                      onChange={() => setFormData(prev => ({...prev, appartient_a: null}))}
                      className="w-4 h-4 text-gray-600"
                    />
                    <span className="text-sm text-gray-700 font-medium">{companyName ? `Moi (${companyName})` : 'Moi'}</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer p-2 rounded border-2 border-blue-300 bg-blue-50">
                    <input
                      type="radio"
                      name="appartient_a"
                      checked={formData.appartient_a === 'cyril'}
                      onChange={() => setFormData(prev => ({...prev, appartient_a: 'cyril'}))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-blue-700 font-medium">🔵 Cyril</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer p-2 rounded border-2 border-violet-300 bg-violet-50">
                    <input
                      type="radio"
                      name="appartient_a"
                      checked={formData.appartient_a === 'ben'}
                      onChange={() => setFormData(prev => ({...prev, appartient_a: 'ben'}))}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-sm text-violet-700 font-medium">🟣 Ben</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingClient(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                  data-testid="cancel-client-btn"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="save-client-btn"
                >
                  {editingClient ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManager;